import React, { useState, useEffect, useRef } from 'react';
import { fetchProfile, updateProfile, changePassword } from '../services/profileService';
import { logout as logoutApi } from '../services/authService';
import { logout as logoutAction } from '../store/slices/authSlice';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useCachedPermissions } from '../hooks/useCachedPermissions';
import TextInput from './TextInput';
import Dialog from './Dialog';
import ManageAdmins from './ManageAdmins';
import styles from './SettingsTabContent.module.css';

const SettingsTabContent = () => {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editedProfile, setEditedProfile] = useState({});
    const [updateLoading, setUpdateLoading] = useState(false);
    const [selectedProfileImage, setSelectedProfileImage] = useState(null);
    const [previewImageUrl, setPreviewImageUrl] = useState(null);

    // Password change state
    const [showPasswordDialog, setShowPasswordDialog] = useState(false);
    const [passwordData, setPasswordData] = useState({
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [passwordError, setPasswordError] = useState('');

    // Logout state
    const [showLogoutDialog, setShowLogoutDialog] = useState(false);
    const [logoutLoading, setLogoutLoading] = useState(false);

    // Manage Admins state
    const [showManageAdmins, setShowManageAdmins] = useState(false);

    // Profile picture state
    const [profilePictureLoading, setProfilePictureLoading] = useState(false);
    const [profilePictureError, setProfilePictureError] = useState('');
    const [profilePictureSuccess, setProfilePictureSuccess] = useState('');
    const fileInputRef = useRef(null);

    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Get permissions
    const { hasShowAdmins } = useCachedPermissions();

    useEffect(() => {
        fetchProfileData();
    }, []);

    // Cleanup preview URL on component unmount
    useEffect(() => {
        return () => {
            if (previewImageUrl) {
                URL.revokeObjectURL(previewImageUrl);
            }
        };
    }, [previewImageUrl]);

    const fetchProfileData = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetchProfile();
            console.log('Fetch profile response:', response); // Debug log

            // Handle different response structures
            const profileData = response?.data || response;

            if (profileData && typeof profileData === 'object') {
                // Update main profile state
                setProfile(profileData);

                // Initialize edited profile state with current data
                setEditedProfile({
                    name: profileData.name || '',
                    phone: profileData.phone || '',
                    phone_code: profileData.phone_code || '+966',
                    address: profileData.address || ''
                });
            } else {
                throw new Error('Invalid profile data received');
            }
        } catch (err) {
            console.error('Fetch profile error:', err);
            setError(err.message || 'Failed to fetch profile');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = () => {
        setIsEditing(true);
    };

    const handleCancelEdit = () => {
        setIsEditing(false);

        // Reset edited profile to current profile data
        if (profile) {
            setEditedProfile({
                name: profile.name || '',
                phone: profile.phone || '',
                phone_code: profile.phone_code || '+966',
                address: profile.address || ''
            });
        }

        // Clear selected image and preview
        setSelectedProfileImage(null);
        if (previewImageUrl) {
            URL.revokeObjectURL(previewImageUrl);
            setPreviewImageUrl(null);
        }

        // Clear file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }

        // Clear profile picture errors and messages
        setProfilePictureError('');
        setProfilePictureSuccess('');
        setError(null);
    };

    const handleSaveEdit = async () => {
        setUpdateLoading(true);
        try {
            // Prepare profile data including the selected image file
            const profileDataToUpdate = { ...editedProfile };
            if (selectedProfileImage) {
                profileDataToUpdate.file = selectedProfileImage;
            }

            const response = await updateProfile(profileDataToUpdate);
            console.log('Update profile response:', response); // Debug log

            // If the request was successful, update local state with the data user entered
            // Create updated profile object with user's input data
            const updatedProfile = {
                ...profile, // Keep existing profile data
                name: editedProfile.name,
                phone: editedProfile.phone,
                phone_code: editedProfile.phone_code,
                address: editedProfile.address
            };

            // If server returns a profile picture URL and we uploaded an image, use it
            const serverData = response?.data || response;
            if (selectedProfileImage && serverData?.profile_picture) {
                updatedProfile.profile_picture = serverData.profile_picture;
            }

            // Update the main profile state with the updated data
            setProfile(updatedProfile);

            // Keep the edited profile state in sync
            setEditedProfile({
                name: editedProfile.name,
                phone: editedProfile.phone,
                phone_code: editedProfile.phone_code,
                address: editedProfile.address
            });

            setIsEditing(false);

            // Clear selected image and preview
            setSelectedProfileImage(null);
            if (previewImageUrl) {
                URL.revokeObjectURL(previewImageUrl);
                setPreviewImageUrl(null);
            }

            // Clear file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }

            // Clear any errors and show success message
            setProfilePictureError('');
            setError(null);
            setProfilePictureSuccess('Profile updated successfully!');
            setTimeout(() => setProfilePictureSuccess(''), 3000);
        } catch (err) {
            console.error('Update profile error:', err);
            setError(err.message || 'Failed to update profile');
        } finally {
            setUpdateLoading(false);
        }
    };

    const handleInputChange = (field, value) => {
        setEditedProfile(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handlePasswordChange = () => {
        setShowPasswordDialog(true);
        setPasswordError('');
        setPasswordData({
            oldPassword: '',
            newPassword: '',
            confirmPassword: ''
        });
    };

    const handleManageAdmins = () => {
        setShowManageAdmins(true);
    };

    const handleBackFromManageAdmins = () => {
        setShowManageAdmins(false);
    };

    const handlePasswordInputChange = (field, value) => {
        setPasswordData(prev => ({
            ...prev,
            [field]: value
        }));
        setPasswordError('');
    };

    const handlePasswordSubmit = async () => {
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setPasswordError('New password and confirm password do not match');
            return;
        }

        if (passwordData.newPassword.length < 6) {
            setPasswordError('New password must be at least 6 characters long');
            return;
        }

        setPasswordLoading(true);
        try {
            await changePassword({
                oldPassword: passwordData.oldPassword,
                newPassword: passwordData.newPassword,
                confirmPassword: passwordData.confirmPassword
            });
            setShowPasswordDialog(false);
            setPasswordData({
                oldPassword: '',
                newPassword: '',
                confirmPassword: ''
            });
        } catch (err) {
            setPasswordError(err.message || 'Failed to change password');
        } finally {
            setPasswordLoading(false);
        }
    };

    const handleLogout = () => {
        setShowLogoutDialog(true);
    };

    const handleLogoutConfirm = async () => {
        setLogoutLoading(true);
        try {
            // 1. Clear ALL localStorage data (not just tokens)
            localStorage.clear();

            // 2. Clear ALL sessionStorage data
            sessionStorage.clear();

            // 3. Clear Redux state
            dispatch(logoutAction());

            // 4. Clear any cached API data and cookies
            try {
                // Clear all cookies
                document.cookie.split(";").forEach(cookie => {
                    const eqPos = cookie.indexOf("=");
                    const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
                    document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
                });
            } catch (cookieError) {
                console.warn('Failed to clear cookies:', cookieError);
            }

            // 5. API call to logout server-side (even if it fails, we'll still logout locally)
            try {
                await logoutApi();
            } catch (apiError) {
                console.error('Logout API error:', apiError);
                // Continue with logout even if API fails
            }

            // 6. Clear the entire browser navigation history
            // This prevents going back to authenticated pages
            window.history.replaceState(null, '', '/login');

            // 7. Force page reload to ensure complete state cleanup
            window.location.href = '/login';

        } catch (err) {
            console.error('Logout error:', err);
            // Even if anything fails, ensure we do a complete cleanup
            localStorage.clear();
            sessionStorage.clear();
            dispatch(logoutAction());

            // Force navigation to login
            window.location.href = '/login';
        } finally {
            setLogoutLoading(false);
        }
    }; const handleLogoutCancel = () => {
        setShowLogoutDialog(false);
    };

    const handleProfilePictureClick = () => {
        fileInputRef.current?.click();
    };

    const handleProfilePictureChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Clear previous messages
        setProfilePictureError('');
        setProfilePictureSuccess('');
        setError(null);

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            setProfilePictureError('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
            return;
        }

        // Validate file size (max 5MB)
        const maxSize = 6 * 1024 * 1024; // 5MB in bytes
        if (file.size > maxSize) {
            setProfilePictureError('File size must be less than 5MB');
            return;
        }

        // Store the selected file and create preview URL
        setSelectedProfileImage(file);
        const previewUrl = URL.createObjectURL(file);
        setPreviewImageUrl(previewUrl);

        // Enable editing mode if not already enabled
        if (!isEditing) {
            setIsEditing(true);
        }
    };

    if (loading) {
        return (
            <div className={styles.settingsContainer}>
                <div className={styles.headerContainer}>
                    <div className={styles.headerLeft}>
                        <h2 className={styles.headerTitle}>Settings</h2>
                    </div>
                    <div className={styles.headerActions}>
                        <button className={styles.actionButton} onClick={handleLogout}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                                <polyline points="16,17 21,12 16,7" />
                                <line x1="21" y1="12" x2="9" y2="12" />
                            </svg>
                            Logout
                        </button>
                    </div>
                </div>

                <div className={styles.loadingMessage}>
                    <h3>Loading...</h3>
                    <p>Fetching profile data...</p>
                </div>
            </div>
        );
    }

    if (error && !profile) {
        return (
            <div className={styles.settingsContainer}>
                <div className={styles.errorMessage}>
                    <h3>Error</h3>
                    <p>{error}</p>
                    <button onClick={fetchProfileData} className={styles.retryButton}>
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.settingsContainer}>
            {showManageAdmins ? (
                <ManageAdmins onBack={handleBackFromManageAdmins} />
            ) : (
                <>
                    <div className={styles.headerContainer}>
                        <div className={styles.headerLeft}>
                            <h2 className={styles.headerTitle}>Settings</h2>
                        </div>
                        <div className={styles.headerActions}>
                            <button className={styles.actionButton} onClick={handleLogout}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                                    <polyline points="16,17 21,12 16,7" />
                                    <line x1="21" y1="12" x2="9" y2="12" />
                                </svg>
                                Logout
                            </button>
                            {!isEditing ? (
                                <button className={styles.actionButton} onClick={handleEdit}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                    </svg>
                                    Edit
                                </button>
                            ) : (
                                <>
                                    <button className={styles.cancelButton} onClick={handleCancelEdit}>
                                        Cancel
                                    </button>
                                    <button
                                        className={styles.saveButton}
                                        onClick={handleSaveEdit}
                                        disabled={updateLoading}
                                    >
                                        {updateLoading ? 'Saving...' : 'Save'}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    <div className={styles.contentContainer}>
                        <div className={styles.leftSection}>
                            <div className={styles.profileSection}>
                                <div className={styles.profileHeader}>
                                    <div className={styles.profilePicture} onClick={handleProfilePictureClick}>
                                        <img
                                            src={previewImageUrl || profile?.profile_picture || 'https://static.vecteezy.com/system/resources/previews/005/544/718/original/profile-icon-design-free-vector.jpg'}
                                            alt="Profile"
                                        />
                                        <div className={styles.profilePictureOverlay}>
                                            {profilePictureLoading ? (
                                                <div className={styles.loadingSpinner}>Uploading...</div>
                                            ) : (
                                                <div className={styles.cameraIcon}>📷</div>
                                            )}
                                        </div>
                                    </div>
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        style={{ display: 'none' }}
                                        accept="image/*"
                                        onChange={handleProfilePictureChange}
                                    />
                                    <h3 className={styles.profileName}>{profile?.name || 'Admin'}</h3>
                                    {profilePictureError && (
                                        <div className={styles.errorMessage}>
                                            {profilePictureError}
                                        </div>
                                    )}
                                    {profilePictureSuccess && (
                                        <div className={styles.successMessage}>
                                            {profilePictureSuccess}
                                        </div>
                                    )}
                                </div>

                                <div className={styles.profileFields}>
                                    {isEditing ? (
                                        <>
                                            <TextInput
                                                label="Name"
                                                value={editedProfile.name}
                                                onChange={(e) => handleInputChange('name', e.target.value)}
                                                placeholder="Enter name"
                                            />
                                            <div className={styles.fieldGroup}>
                                                <label className={styles.fieldLabel}>Phone Code</label>
                                                <select
                                                    value={editedProfile.phone_code}
                                                    onChange={(e) => handleInputChange('phone_code', e.target.value)}
                                                    className={styles.selectInput}
                                                >
                                                    <option value="+966">+966 (Saudi Arabia)</option>
                                                    <option value="+971">+971 (UAE)</option>
                                                    <option value="+965">+965 (Kuwait)</option>
                                                    <option value="+973">+973 (Bahrain)</option>
                                                    <option value="+968">+968 (Oman)</option>
                                                    <option value="+974">+974 (Qatar)</option>
                                                    <option value="+20">+20 (Egypt)</option>
                                                    <option value="+1">+1 (USA/Canada)</option>
                                                    <option value="+44">+44 (UK)</option>
                                                    <option value="+33">+33 (France)</option>
                                                    <option value="+49">+49 (Germany)</option>
                                                </select>
                                            </div>
                                            <TextInput
                                                label="Phone"
                                                value={editedProfile.phone}
                                                onChange={(e) => handleInputChange('phone', e.target.value)}
                                                placeholder="Enter phone number"
                                            />
                                            <TextInput
                                                label="Address"
                                                value={editedProfile.address}
                                                onChange={(e) => handleInputChange('address', e.target.value)}
                                                placeholder="Enter address"
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <div className={styles.fieldDisplay}>
                                                <label>Name:</label>
                                                <span>{profile?.name || 'N/A'}</span>
                                            </div>
                                            <div className={styles.fieldDisplay}>
                                                <label>Phone:</label>
                                                <span>
                                                    {profile?.phone_code && profile?.phone
                                                        ? `${profile.phone_code}${profile.phone}`
                                                        : profile?.phone || 'N/A'}
                                                </span>
                                            </div>
                                            <div className={styles.fieldDisplay}>
                                                <label>Gender:</label>
                                                <span>{profile?.gender || 'N/A'}</span>
                                            </div>
                                            <div className={styles.fieldDisplay}>
                                                <label>Address:</label>
                                                <span>{profile?.address || 'N/A'}</span>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className={styles.rightSection}>
                            <div className={styles.passwordSection}>
                                <h3>Security</h3>
                                <button className={styles.changePasswordButton} onClick={handlePasswordChange}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M15.6 2.7a10 10 0 1 0 5.7 5.7" />
                                        <circle cx="12" cy="12" r="2" />
                                        <path d="M13.4 10.6 19 5" />
                                    </svg>
                                    Change Password
                                </button>

                                {hasShowAdmins && (
                                    <button className={styles.manageAdminsButton} onClick={handleManageAdmins}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                                            <circle cx="9" cy="7" r="4" />
                                            <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                                            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                        </svg>
                                        Manage Admins
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Password Change Dialog */}
                    <Dialog
                        open={showPasswordDialog}
                        onClose={() => setShowPasswordDialog(false)}
                        title="Change Password"
                    >
                        <div className={styles.passwordDialogContent}>
                            {passwordError && (
                                <div className={styles.errorAlert}>
                                    {passwordError}
                                </div>
                            )}

                            <TextInput
                                type="password"
                                label="Current Password"
                                value={passwordData.oldPassword}
                                onChange={(e) => handlePasswordInputChange('oldPassword', e.target.value)}
                                placeholder="Enter current password"
                            />

                            <TextInput
                                type="password"
                                label="New Password"
                                value={passwordData.newPassword}
                                onChange={(e) => handlePasswordInputChange('newPassword', e.target.value)}
                                placeholder="Enter new password"
                            />

                            <TextInput
                                type="password"
                                label="Confirm New Password"
                                value={passwordData.confirmPassword}
                                onChange={(e) => handlePasswordInputChange('confirmPassword', e.target.value)}
                                placeholder="Confirm new password"
                            />

                            <div className={styles.dialogActions}>
                                <button
                                    className={styles.cancelButton}
                                    onClick={() => setShowPasswordDialog(false)}
                                    disabled={passwordLoading}
                                >
                                    Cancel
                                </button>
                                <button
                                    className={styles.saveButton}
                                    onClick={handlePasswordSubmit}
                                    disabled={passwordLoading || !passwordData.oldPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                                >
                                    {passwordLoading ? 'Changing...' : 'Change Password'}
                                </button>
                            </div>
                        </div>
                    </Dialog>

                    {/* Logout Confirmation Dialog */}
                    <Dialog
                        open={showLogoutDialog}
                        onClose={handleLogoutCancel}
                        title="Confirm Logout"
                    >
                        <div className={styles.logoutDialogContent}>
                            <p>Are you sure you want to logout?</p>
                            <div className={styles.dialogActions}>
                                <button
                                    className={styles.cancelButton}
                                    onClick={handleLogoutCancel}
                                    disabled={logoutLoading}
                                >
                                    Cancel
                                </button>
                                <button
                                    className={styles.logoutButton}
                                    onClick={handleLogoutConfirm}
                                    disabled={logoutLoading}
                                >
                                    {logoutLoading ? 'Logging out...' : 'Logout'}
                                </button>
                            </div>
                        </div>
                    </Dialog>
                </>
            )}
        </div>
    );
};

export default SettingsTabContent;
